import { Component, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { saveAs } from 'file-saver';
import { orderBy } from 'lodash';
import { DatePipe } from '@angular/common';

import { ACTION } from "../../shared/constant/action.constant";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { ParticipationFormService } from "../../shared/service/participation-form.service";
import { PrintService } from "../../shared/service/print.service";

const ROLES = require("../../../../../server/shared/constants/role.constant");

@Component({
  selector: 'participation-form-list',
  templateUrl: './participation-form-list.component.html',
  styleUrls: ['./participation-form-list.component.scss'],
})

export class ParticipationFormListComponent implements OnInit {
  profile$: Observable<any>;
  currentUser: any;
  participationForm$: Observable<any>;
  readMore: boolean = true;
  loading: boolean = true;
  participationForms: any = [];
  tableData: any = [];
  visibleTableData: any = [];
  term: string = null;
  pageEvent: any;
  page: number = 1;
  pageSize: number = 50;
  showDeleted: boolean = false;

  columns: any[] = [
    {
      display: 'Campus',
      variable: 'campus',
      styleClass: 'hidden-sm hidden-xs',
      minWidth: '15%',
      wch: 25
    },
    {
      display: 'Unit Name',
      variable: 'unit',
      styleClass: '',
      minWidth: '20%',
      wch: 50
    },
    {
      display: 'Primary Contact',
      variable: 'primaryContact',
      styleClass: 'hidden-xs',
      minWidth: '15%',
      wch: 25
    },
    {
      display: 'Status',
      variable: 'statusValue',
      styleClass: '',
      minWidth: '10%',
      wch: 20
    },
    {
      display: 'Submitter',
      variable: 'createdBy',
      styleClass: 'hidden-sm hidden-xs',
      minWidth: '15%',
      wch: 25
    },
    {
      display: 'Submitted',
      variable: 'submittedDate',
      styleClass: 'hidden-xs',
      minWidth: '7.5%',
      wch: 15
    },
    {
      display: 'Program Name',
      variable: 'programName',
      styleClass: '',
      minWidth: '7.5%',
      wch: 15
    }
  ];

  sort: any = {
    column: 'submittedDate',
    descending: true
  };

  constructor(private datePipe: DatePipe, private store: Store<any>, private printService: PrintService, private participationFormService: ParticipationFormService) {
  }

  ngOnInit(): void {
    this.profile$ = this.store.select('PERSON_REDUCER');

    this.profile$.subscribe(s => {
      this.currentUser = s.user;
    });

    this.loadForms();
  }

  loadForms(): void {
    this.loading = true;
    this.participationFormService.getParticipationForms(this.showDeleted).then((forms) => {
      this.participationForms = forms || [];
      this.loading = false;
      // Re-apply the active search term and sort so toggling doesn't reset the user's view.
      this.filter(this.term);
    });
  }

  toggleShowDeleted(): void {
    this.showDeleted = !this.showDeleted;
    this.page = 1;
    this.loadForms();
  }

  // Deleted programs are for reference only, so the control is offered to the roles that
  // administer the program rather than to the units themselves (SLACKERS-579). The server
  // re-checks this; hiding the control is a convenience, not the access boundary.
  canViewDeleted(): boolean {
    return (
      this.currentUser &&
      [ROLES.ROLE_UCOP_ADMIN, ROLES.ROLE_APP_ADMIN, ROLES.ROLE_CAMPUS_ADMIN].indexOf(this.currentUser.role) > -1
    );
  }

  isDeleted(participationForm): boolean {
    return participationForm && participationForm.status === 'INACTIVATED';
  }

  selectedClass(columnName) {
    return columnName === this.sort.column ? 'sortable text-nowrap sort-' + this.sort.descending : 'sortable text-nowrap';
  }

  changeSorting(columnName): void {
    if (this.sort.column === columnName) {
      this.sort.descending = !this.sort.descending;
    } else {
      this.sort.column = columnName;
      this.sort.descending = false;
    }
    this.tableData = orderBy(this.tableData, [this.sort.column], [this.sort.descending ? 'desc' : 'asc']);
  }

  filter(term: any): any {
    term = term ? term.toLowerCase() : null;
    let result = [];

    if (term && this.participationForms && this.participationForms) {
      result = term ? this.participationForms.filter((participationForm: any) =>
        (participationForm.primaryContact && participationForm.primaryContact.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.createdBy && participationForm.createdBy.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.campus && participationForm.campus.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.unit && participationForm.unit.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.statusValue && participationForm.statusValue.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.supplier && participationForm.supplier.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.submittedDate && participationForm.submittedDate.toLowerCase().indexOf(term) !== -1) ||
        (participationForm.programName && participationForm.programName.toLowerCase().indexOf(term) !== -1)) : this.participationForms;
    } else {
      result = this.participationForms;
    }


    this.tableData = orderBy(result, [this.sort.column], [this.sort.descending ? 'desc' : 'asc']);
  }

  export(data, term) {
    const filename = `unit_status_${term ? `${term}_` : ''}${new Date()
      .toLocaleDateString()
      .replace(/\//g, '_')}.xlsx`;

    const headerDef = {};
    const colsDef = [];
    const rowDef = [{
      hpx: 20
    }];
    this.columns.forEach(column => {
      headerDef[column.variable] = this.sort.column === column.variable ? `${column.display} ${this.sort.descending ? '↓' : '↑'}` : column.display;
      colsDef.push({
        wch: column.wch
      });
    });

    const formattedData = [...data];
    formattedData.map(participationForm => {
      participationForm.submittedDate = this.datePipe.transform(participationForm.submittedDate, 'MM/dd/yyyy');
    });

    this.printService.printXlsx({
      filename,
      data: [
        {
          name: 'unit status',
          data: [headerDef, ...formattedData],
          options: {
            header: Object.keys(headerDef),
            skipHeader: true,
            '!cols': colsDef,
            '!rows': rowDef
          }
        }
      ]
    }).then(blob => saveAs(blob, filename)).catch(error => console.error(error));
  }

  inactivateParticipationForm(form) {
    if (confirm('Are you sure you want to delete this whole program? Changes will be permanent. Select Cancel if you do not want to delete, or select OK if you want to delete. You will need to re-load the page to see the change on this page.')) {
      this.participationForm$ = this.store.select('PARTICIPATION_FORM_REDUCER');
      this.store.dispatch({
        type: ACTION.PARTICIPATION_FORM_INACTIVATE,
        payload: { formSubmissionCode: form.formSubmissionCode }
      });
      this.participationForm$.filter(state => state.status === "COMPLETED").subscribe((state: any) => {
        this.store.dispatch({ type: ACTION.LOAD_PARTICIPATION_FORMS, payload: {} });
      });
    }
  }

  getLink(groupId, campusCode): string {
    return environment.GROUP_TRACKABLES_URL + '/group-trackables/' + groupId + '?campusCode=' + campusCode;
  }

}



// WEBPACK FOOTER //
// /home/runner/work/slipresistant/slipresistant/client/src/app/participation-form/participation-form-list/participation-form-list.component.ts